import { indexToMonth } from "@dashboard/utils/months";
import { useTranslation } from "react-i18next";
import { NewActivityButton } from "../../features/Dashboard/AnnualCycle/NewActivityButton";
import { cn } from "frontend-components";
import { useMemo } from "react";
import { YearNavigation } from "../YearNavigation";

const MONTH_COUNT = 12;

type YearlyCycleLineProps = {
    onMonthClick?: (monthIndex: number) => void;
    selectedMonths?: number[];
    year: number;
    onYearChange?: (newYear: number) => void;
};

export default function YearlyCycleLine({
    year,
    onMonthClick,
    selectedMonths = [],
    onYearChange,
}: YearlyCycleLineProps) {
    const { t } = useTranslation();

    const active = useMemo(() => {
        return new Set(selectedMonths);
    }, [selectedMonths]);

    const handleMonthClick = (idx: number) => {
        if (onMonthClick) {
            onMonthClick(idx);
        }
    };

    return (
        <div className="rounded-[3px] border border-compendia-border bg-compendia-card p-4 xl:border-0 xl:bg-transparent xl:p-0">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <YearNavigation year={year} onYearChange={onYearChange} showTitle={true} />
                <NewActivityButton className="hidden xl:inline-flex" />
            </div>

            <div
                role="group"
                aria-label={t("dashboard.monthFilter")}
                className="grid grid-cols-4 gap-2 md:grid-cols-6 xl:grid-cols-12 xl:gap-3"
            >
                {Array.from({ length: MONTH_COUNT }, (_, idx) => {
                    const monthKey = indexToMonth[idx + 1];
                    const isSelected = active.has(idx);

                    return (
                        <button
                            key={monthKey}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => handleMonthClick(idx)}
                            className={cn(
                                "relative flex items-center justify-center rounded-[3px] px-2 py-6",
                                "text-sm font-semibold whitespace-nowrap text-compendia-foreground transition-colors",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2",
                                "2xl:px-3 2xl:py-7 2xl:text-base",
                                isSelected
                                    ? "bg-chameleon-green ring-2 ring-chameleon-green-40"
                                    : "bg-compendia-greige-20 hover:bg-compendia-greige-40",
                            )}
                        >
                            <span className="2xl:hidden">
                                {t(`common.month.short.${monthKey}`)}
                            </span>
                            <span className="hidden 2xl:inline">
                                {t(`common.month.long.${monthKey}`)}
                            </span>
                            {idx < MONTH_COUNT - 1 && (
                                <span
                                    aria-hidden="true"
                                    className="pointer-events-none absolute left-full top-1/2 hidden h-px w-3 bg-compendia-border xl:block"
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
